import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { ApiBeneficiario } from "../api"

export interface AddedBeneficiario {
  id: string
  numeroPoliza: number
  tipoBeneficiario: string
  rut: number
  dv: string
  nombre: string
  apellidoPaterno: string
  apellidoMaterno: string
  fechaNacimiento: string
  parentesco: string
  email: string
  region: string
  ciudad: string
  comuna: string
  direccion: string
  casaDepto: string
  celular: string
  porcentaje: number
}

export interface ModifiedBeneficiario extends ApiBeneficiario {
  numeroPoliza: number
  email?: string
  celular?: string
  direccion?: string
}

export interface DeletedBeneficiario extends ApiBeneficiario {
  numeroPoliza: number
  email?: string
  celular?: string
  direccion?: string
}

interface BeneficiariosState {
  // State
  added: AddedBeneficiario[]
  modified: ModifiedBeneficiario[]
  deleted: DeletedBeneficiario[]

  // Actions
  addBeneficiario: (beneficiario: AddedBeneficiario) => void
  updateBeneficiario: (id: string, beneficiario: Partial<AddedBeneficiario>) => void
  removeBeneficiario: (id: string) => void

  modifyApiBeneficiario: (beneficiario: ModifiedBeneficiario) => void
  deleteApiBeneficiario: (beneficiario: DeletedBeneficiario) => void
  restoreApiBeneficiario: (beneficiarioId: number) => void

  updateBeneficiarioPercentage: (numeroPoliza: number, beneficiarioId: number, newPercentage: number) => void

  hasChanges: () => boolean
  hasChangesForPoliza: (numeroPoliza: number) => boolean
  getChangesForPoliza: (numeroPoliza: number) => {
    added: AddedBeneficiario[]
    modified: ModifiedBeneficiario[]
    deleted: DeletedBeneficiario[]
  }

  clearAll: () => void
  clearPoliza: (numeroPoliza: number) => void
  clearBeneficiarios: () => void
}

export const useBeneficiariosStore = create<BeneficiariosState>()(
  persist(
    (set, get) => ({
      // Initial state
      added: [],
      modified: [],
      deleted: [],

      // Add a new beneficiary
      addBeneficiario: (beneficiario) => {
        set((state) => ({
          added: [...state.added, beneficiario],
        }))
      },

      // Update an added beneficiary
      updateBeneficiario: (id, updates) => {
        set((state) => ({
          added: state.added.map((b) => (b.id === id ? { ...b, ...updates } : b)),
        }))
      },

      // Remove an added beneficiary
      removeBeneficiario: (id) => {
        set((state) => ({
          added: state.added.filter((b) => b.id !== id),
        }))
      },

      // Modify an API beneficiary
      modifyApiBeneficiario: (beneficiario) => {
        set((state) => {
          const existing = state.modified.find((b) => b.id === beneficiario.id)
          if (existing) {
            return {
              modified: state.modified.map((b) => (b.id === beneficiario.id ? beneficiario : b)),
            }
          }
          return {
            modified: [...state.modified, beneficiario],
          }
        })
      },

      // Delete an API beneficiary
      deleteApiBeneficiario: (beneficiario) => {
        set((state) => ({
          deleted: [...state.deleted.filter((b) => b.id !== beneficiario.id), beneficiario],
          // Remove from modified if it was there
          modified: state.modified.filter((b) => b.id !== beneficiario.id),
        }))
      },

      // Restore a deleted API beneficiary
      restoreApiBeneficiario: (beneficiarioId) => {
        set((state) => ({
          deleted: state.deleted.filter((b) => b.id !== beneficiarioId),
        }))
      },

      // Update beneficiary percentage (for redistribution)
      updateBeneficiarioPercentage: (numeroPoliza, beneficiarioId, newPercentage) => {
        set((state) => {
          // Check if it's in modified
          const modifiedBen = state.modified.find((b) => b.id === beneficiarioId && b.numeroPoliza === numeroPoliza)
          if (modifiedBen) {
            return {
              modified: state.modified.map((b) =>
                b.id === beneficiarioId && b.numeroPoliza === numeroPoliza ? { ...b, porcentaje: newPercentage } : b,
              ),
            }
          }

          // If not in modified, we need to add it (it's an API beneficiary being modified)
          // This should be called with the full beneficiary object
          return state
        })
      },

      // Check if there are any changes
      hasChanges: () => {
        const state = get()
        return state.added.length > 0 || state.modified.length > 0 || state.deleted.length > 0
      },

      // Check if there are changes for a specific policy
      hasChangesForPoliza: (numeroPoliza) => {
        const state = get()
        return (
          state.added.some((b) => b.numeroPoliza === numeroPoliza) ||
          state.modified.some((b) => b.numeroPoliza === numeroPoliza) ||
          state.deleted.some((b) => b.numeroPoliza === numeroPoliza)
        )
      },

      // Get changes for a specific policy
      getChangesForPoliza: (numeroPoliza) => {
        const state = get()
        return {
          added: state.added.filter((b) => b.numeroPoliza === numeroPoliza),
          modified: state.modified.filter((b) => b.numeroPoliza === numeroPoliza),
          deleted: state.deleted.filter((b) => b.numeroPoliza === numeroPoliza),
        }
      },

      // Clear all changes
      clearAll: () => {
        set({
          added: [],
          modified: [],
          deleted: [],
        })
      },

      // Clear changes for a specific policy
      clearPoliza: (numeroPoliza) => {
        set((state) => ({
          added: state.added.filter((b) => b.numeroPoliza !== numeroPoliza),
          modified: state.modified.filter((b) => b.numeroPoliza !== numeroPoliza),
          deleted: state.deleted.filter((b) => b.numeroPoliza !== numeroPoliza),
        }))
      },

      // Clear changes for a specific policy
      clearBeneficiarios: () => {
        set({
          added: [],
          modified: [],
          deleted: [],
        })
      },
    }),
    {
      name: "beneficiarios-storage",
    },
  ),
)
